// Copyright ©️ 2026 eVolve MEP, LLC

import { dateOnly, type EvolveApiRoute, type PageFetcher, type AdditionalRequestConfig } from '@evolvemep/api-ts';
import type { FilterModel, IServerSideGetRowsRequest } from 'ag-grid-enterprise';
import { isBefore, parseISO } from 'date-fns';

import type { CustomValueSetFilterProps } from 'helpers/ag-grid/CustomSetFilterComponent';
import type { KeysOfType } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';
import { strongEntries, strongFromEntries } from 'helpers/strongEntries';

// AG Grid's FilterModel type is super vague and unhelpful
// This is a reverse-engineered Type for their built-in model,
// with our own custom filterTypes added.
type HandledFilterTypes = 'set' | 'text' | 'date' | 'number' | 'custom';
export type HandledFilterModel<T> = {
  filterType: HandledFilterTypes;
} & (
  | {
      filterType: 'set';
      values: T[] | T[][];
      filterKey?: string;
    }
  | {
      filterType: 'text';
      filter: string;
    }
  | {
      filterType: 'date';
      dateFrom: string;
      dateTo: string;
    }
  | {
      filterType: 'number';
      filter: number;
      filterTo: number;
    }
  | {
      filterType: 'custom';
      value: T;
      filterKey?: string;
    }
);

/** Used to convert an AG Grid FilterModel to an Evolve API request */
const convertFilterToEvolveApi = <
  // Unfortunately, FilterModel isn't typed any stronger than `any`,
  // so we're doing our best to keep this as type-safe as possible
  S extends HandledFilterTypes,
>(
  /** Filter Model */
  [key, filterModel]: readonly [string | number, HandledFilterModel<S>],
  /** Any additional params that will eventually be sent to the backend */
  additionalParams?: Record<string, unknown>,
):
  // Returns a tuple of [key, value | value[]]
  [string, string | string[]] | null => {
  const filterKey = String('filterKey' in filterModel && !!filterModel.filterKey ? filterModel.filterKey : key);
  if (filterModel.filterType === 'set') {
    if (filterModel.values.length > 0) {
      const additionalParamsForSetFilter = additionalParams?.[filterKey];
      return [
        filterKey,
        filterModel.values
          // filterModel may be an array of arrays, but we want
          // a flat array to send to the backend.
          .flatMap((v) => v)
          // if we have additional params for our same key,
          // we only want to send the intersection of the two sets.
          .filter(
            (val) =>
              !Array.isArray(additionalParamsForSetFilter) ||
              additionalParamsForSetFilter.length === 0 ||
              additionalParamsForSetFilter.includes(val),
          )
          .map(encodeURIComponent),
      ];
    }
    return null;
  }
  if (filterModel.filterType === 'text') {
    return [filterKey, `like:${encodeURIComponent(filterModel.filter)}`];
  }
  if (filterModel.filterType === 'date') {
    // oxlint-disable-next-line typescript/no-non-null-assertion
    const startDate = parseISO(filterModel.dateFrom.split(' ')[0]!);
    // oxlint-disable-next-line typescript/no-non-null-assertion
    const endDate = parseISO(filterModel.dateTo.split(' ')[0]!);
    const [startDateOnly, endDateOnly] = (
      isBefore(startDate, endDate) ? [startDate, endDate] : [endDate, startDate]
    ).map(dateOnly);
    return [filterKey, `start:${startDateOnly}end:${endDateOnly}`];
  }
  if (filterModel.filterType === 'number') {
    const { filter, filterTo } = filterModel;
    const [from, to] = filterTo > filter ? [filter, filterTo] : [filterTo, filter];
    return [filterKey, `start:${from}end:${to}`];
  }
  // oxlint-disable-next-line typescript/no-unnecessary-condition
  if (filterModel.filterType === 'custom') {
    return [filterKey, encodeURIComponent(filterModel.value)];
  }
  // @ts-expect-error The type system believes the above are the only possible values of filterType,
  // but there are filterTypes AG Grid has that we don't handle.
  // oxlint-disable-next-line no-console
  console.warn(`Unrecognized filterModel.filterType '${filterModel.filterType}', ignoring`);
  return null;
};

export const convertAgGridRequestToParams = <Route extends EvolveApiRoute<'GET_PAGINATED'>>(
  request: IServerSideGetRowsRequest,
  additionalConfig?: AdditionalRequestConfig<'GET_PAGINATED', Route>,
): Parameters<PageFetcher<Route>> => {
  const skip = request.startRow ?? 0;
  const filterParams = strongFromEntries(
    strongEntries((request.filterModel ?? {}) as FilterModel)
      .map((filter) => convertFilterToEvolveApi(filter, additionalConfig?.params))
      .filter(isNotNil),
  );
  return [
    {
      skip,
      take: isNotNil(request.endRow) ? request.endRow - skip : undefined,
      orderBy: request.sortModel.map(({ colId, sort }) => `${colId}:${sort}` as const),
    },
    {
      ...additionalConfig,
      params: {
        ...additionalConfig?.params,
        ...filterParams,
      },
    } as AdditionalRequestConfig<'GET_PAGINATED', Route>,
  ] as const;
};

/**
 * Used to generate the proper CustomSetFilter params for a given set of values.
 *
 * @param values List of possible values
 * @param idKey The key of the value object that will be used as the filter value
 * @param labelKey The key of the value object that will be used as the display label
 * @param opts Additional options object.
 * @param opts.labelDescriptor A function that returns a label description to appear in parentheses
 * @param opts.filterKey The key of the query parameter to pass to the backend for this filter
 */
export const customSetFilterParams = <
  TData,
  T,
  IdKey extends KeysOfType<T, string>,
  NameKey extends KeysOfType<T, string>,
>(
  values: T[],
  idKey: IdKey,
  labelKey: NameKey | ((t: T) => string),
  opts: {
    filterKey?: string;
    labelDescriptor?: (t: T) => string | null | undefined;
  } = {},
): CustomValueSetFilterProps<TData, T, T[IdKey]> => ({
  values,
  valueFormatter: ({ value }) =>
    isNotNil(value) ? (typeof labelKey === 'function' ? labelKey(value) : (value[labelKey] as string)) : null,
  labelDescriptor: ({ value }) => (isNotNil(value) ? opts.labelDescriptor?.(value) : undefined),
  keyCreator: ({ value }) => value?.[idKey],
  filterKey: opts.filterKey,
  suppressSorting: true,
  buttons: ['apply', 'reset'],
});
